/**
 * Ball path derivation for the broadcast live pitch (PMA-BCAST-004).
 *
 * Ball data is discrete: events carry `location` [x, y] in the 120x80 pitch
 * frame; passes carry `data.end_location`. One absolute frame, home attacks
 * +x (no side switch). The ball "moves" from event to event; shots travel to
 * the attacked goal; there is no optical tracking.
 */
import type { MatchEvent } from "../../store/useMatchLogStore";
import { PITCH_HEIGHT, PITCH_WIDTH } from "../logger/utils/heatMapZones";

export type Point = [number, number];
export type SegmentKind = "pass" | "shot" | "move";

export interface BallSegment {
  id: string;
  kind: SegmentKind;
  from: Point;
  to: Point;
  teamId: string;
  eventType: string;
  playerId?: string;
}

export interface BallPath {
  /** Ball resting position after the last event, null before any location. */
  ball: Point | null;
  /** All segments, oldest first. */
  segments: BallSegment[];
}

const isPoint = (v: unknown): v is Point =>
  Array.isArray(v) &&
  v.length >= 2 &&
  typeof v[0] === "number" &&
  typeof v[1] === "number" &&
  Number.isFinite(v[0]) &&
  Number.isFinite(v[1]);

const clampPoint = ([x, y]: Point): Point => [
  Math.min(PITCH_WIDTH, Math.max(0, x)),
  Math.min(PITCH_HEIGHT, Math.max(0, y)),
];

/** Goal centre a shot by `teamId` travels toward (home attacks +x). */
export function shotTarget(
  teamId: string,
  homeTeamId: string | undefined,
): Point {
  const attacksPlusX = homeTeamId !== undefined && teamId === homeTeamId;
  // The trail aims at the goal-mouth centre; the exact placement is shown
  // by the call-out's goal-frame target.
  return [attacksPlusX ? PITCH_WIDTH : 0, PITCH_HEIGHT / 2];
}

const MIN_MOVE = 1;

export function deriveBallPath(
  events: MatchEvent[],
  homeTeamId?: string,
): BallPath {
  const segments: BallSegment[] = [];
  let ball: Point | null = null;

  events.forEach((ev, index) => {
    if (!isPoint(ev.location)) return;
    const loc = clampPoint(ev.location);
    const id = String(ev._id ?? ev.client_id ?? `idx-${index}`);

    // Ball travels from where it last was to where this event happened.
    if (ball && Math.hypot(ball[0] - loc[0], ball[1] - loc[1]) > MIN_MOVE) {
      segments.push({
        id: `${id}-move`,
        kind: "move",
        from: ball,
        to: loc,
        teamId: ev.team_id,
        eventType: ev.type,
        playerId: ev.player_id,
      });
    }

    const end = ev.data?.end_location;
    if (ev.type === "Shot" && ev.data?.own_goal !== true) {
      const to = shotTarget(ev.team_id, homeTeamId);
      segments.push({
        id,
        kind: "shot",
        from: loc,
        to,
        teamId: ev.team_id,
        eventType: ev.type,
        playerId: ev.player_id,
      });
      ball = to;
      return;
    }
    if (isPoint(end)) {
      const to = clampPoint(end);
      segments.push({
        id,
        kind: "pass",
        from: loc,
        to,
        teamId: ev.team_id,
        eventType: ev.type,
        playerId: ev.player_id,
      });
      ball = to;
      return;
    }
    ball = loc;
  });

  return { ball, segments };
}

/** The last `n` segments, each with a fade opacity (newest = 1). */
export function trailSegments(
  segments: BallSegment[],
  n: number,
): Array<BallSegment & { opacity: number }> {
  const count = Math.max(0, Math.min(10, Math.floor(n)));
  if (count === 0) return [];
  const tail = segments.slice(-count);
  return tail.map((s, i) => ({
    ...s,
    opacity: Number(((i + 1) / tail.length).toFixed(3)) * 0.85 + 0.1,
  }));
}
